
import React from "react";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { CurrentAffair } from "./CurrentAffairs";

interface CurrentAffairCardProps {
  article: CurrentAffair;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: (id: string) => void;
  isAdmin: boolean;
}

export const CurrentAffairCard: React.FC<CurrentAffairCardProps> = ({
  article,
  isSelected,
  onSelect,
  onDelete,
  isAdmin,
}) => {
  // Use created_at if available, or current date as fallback
  const timeAgo = article.date ? 
    formatDistanceToNow(new Date(article.date), { addSuffix: true }) :
    formatDistanceToNow(new Date(), { addSuffix: true });
  
  // Clean HTML content to create a preview
  const stripHtml = (html: string) => {
    if (!html) return "";
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || "";
    } catch (e) {
      console.error("Error parsing HTML:", e);
      return html.replace(/<[^>]*>/g, '');
    }
  };
  
  const contentPreview = stripHtml(article.content || '').substring(0, 60) + '...';
  
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete this article: "${article.title}"?`)) {
      onDelete(article.id);
    }
  };
  
  return (
    <div
      className={`rounded-md p-2 mb-1 cursor-pointer transition-colors border ${
        isSelected ? "bg-primary/10 border-primary/20" : "bg-card hover:bg-primary/5 border-transparent"
      }`}
      onClick={onSelect}
    >
      <div className="flex justify-between items-start">
        <h3 className="font-medium text-sm line-clamp-1 text-primary">{article.title}</h3>
        {isAdmin && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive -mt-1 -mr-1"
            onClick={handleDelete}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      
      <div className="flex mt-1 space-x-2 text-xs">
        {article.language && (
          <span className="bg-primary/10 px-1.5 py-0.5 rounded-full text-primary text-[10px]">
            {article.language}
          </span>
        )}
        {article.time_period && (
          <span className="bg-blue-100 px-1.5 py-0.5 rounded-full text-blue-700 text-[10px] capitalize">
            {article.time_period}
          </span>
        )}
      </div>
      
      <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{contentPreview}</p>
      
      <div className="text-[10px] text-muted-foreground mt-1">
        {timeAgo}
      </div>
    </div>
  );
};
